import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Upload, Save, Building2, Bell, Package, FileText, Receipt, Truck, Link2, ExternalLink, Shield, Users } from 'lucide-react';
import { parseApiError } from '../lib/parseApiError';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Stato REALE della connessione Aruba (credenziali configurate sul server)
function ArubaStatoReale() {
  const [stato, setStato] = useState(null);
  useEffect(() => {
    axios.get(`${API}/aruba/stato`).then((r) => setStato(r.data)).catch(() => setStato(null));
  }, []);
  if (!stato) {
    return (
      <div className="p-3 rounded-md flex items-center gap-3 bg-gray-500/10 border border-gray-500/30">
        <div className="w-3 h-3 rounded-full bg-gray-400" />
        <span className="text-sm">Verifica connessione Aruba in corso…</span>
      </div>
    );
  }
  const ok = stato.ws_ok;
  const authOk = stato.auth_ok;
  return (
    <div className="space-y-2" data-testid="aruba-stato-impostazioni">
      <div className="p-3 rounded-md bg-blue-500/10 border border-blue-500/30 text-xs text-muted-foreground">
        Le credenziali Aruba sono <strong>già configurate sul server</strong> (utente {`ARUBA0479...`}): non serve inserirle qui.
      </div>
      <div className={`p-3 rounded-md flex items-start gap-3 ${ok ? 'bg-green-500/10 border border-green-500/30' : authOk ? 'bg-amber-500/10 border border-amber-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
        <div className={`w-3 h-3 mt-1 rounded-full shrink-0 ${ok ? 'bg-green-500 animate-pulse' : authOk ? 'bg-amber-500' : 'bg-red-500'}`} />
        <div className="text-sm space-y-1">
          {ok ? (
            <span className="font-medium text-green-600">Connesso ad Aruba — invio automatico a SdI attivo (ambiente: {stato.ambiente})</span>
          ) : authOk ? (
            <>
              <span className="font-medium text-amber-600">Credenziali valide, ma i Web Service Aruba non sono abilitati sul tuo abbonamento</span>
              <p className="text-xs text-muted-foreground">{stato.errore}</p>
              <p className="text-xs text-muted-foreground">
                Nel frattempo usa il <strong>flusso manuale</strong>: scarica l'XML della fattura (pulsante viola in Documenti)
                e caricalo sul pannello Aruba, poi segna la fattura come inviata (check verde).
              </p>
            </>
          ) : (
            <>
              <span className="font-medium text-red-600">Connessione ad Aruba non riuscita</span>
              <p className="text-xs text-muted-foreground">{stato.errore}</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


function EmailStatsPanel() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const fetchStats = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/email/stats`);
      setStats(r.data);
    } catch (e) { console.error('Errore statistiche email:', e); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchStats(); }, []);
  const TYPE_LABEL = {
    benvenuto: '🆕 Benvenuto cliente',
    ticket: '🎫 Apertura ticket',
    preventivo: '📤 Invio preventivo',
    accettazione: '✅ Notifica accettazione',
    reminder: '⏰ Reminder follow-up',
    scadenza_rt: '⚠️ Scadenza RT',
    test: '🧪 Test',
    generica: '📧 Generica',
  };
  if (!stats) {
    return (
      <div className="bg-muted/30 rounded-md p-4 text-xs text-muted-foreground">
        {loading ? 'Caricamento statistiche...' : 'Statistiche email non disponibili'}
      </div>
    );
  }
  const types = Array.from(new Set([...Object.keys(stats.by_type_7gg || {}), ...Object.keys(stats.by_type_30gg || {})]));
  return (
    <div className="space-y-3" data-testid="email-stats-panel">
      <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground flex items-center gap-2">
        📊 Statistiche invii
        <button onClick={fetchStats} className="text-[10px] underline text-cyan-600 ml-auto">
          {loading ? '...' : '↻ aggiorna'}
        </button>
      </p>
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-gradient-to-br from-cyan-500/10 to-cyan-600/10 border border-cyan-500/30 rounded-md p-4">
          <p className="text-[10px] uppercase tracking-wider text-cyan-700 dark:text-cyan-400">Ultimi 7 giorni</p>
          <p className="text-3xl font-black tabular-nums text-cyan-600 dark:text-cyan-300">{stats.total_7gg}</p>
          <p className="text-[10px] text-muted-foreground">email inviate dal CRM</p>
        </div>
        <div className="bg-gradient-to-br from-emerald-500/10 to-emerald-600/10 border border-emerald-500/30 rounded-md p-4">
          <p className="text-[10px] uppercase tracking-wider text-emerald-700 dark:text-emerald-400">Ultimi 30 giorni</p>
          <p className="text-3xl font-black tabular-nums text-emerald-600 dark:text-emerald-300">{stats.total_30gg}</p>
          <p className="text-[10px] text-muted-foreground">email inviate dal CRM</p>
        </div>
      </div>
      {types.length > 0 && (
        <div className="border border-border rounded-md p-3">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Suddivisione per tipologia (30gg)</p>
          <div className="space-y-1.5">
            {types.map(t => {
              const v30 = stats.by_type_30gg[t] || 0;
              const v7 = stats.by_type_7gg[t] || 0;
              const max = Math.max(...types.map(x => stats.by_type_30gg[x] || 0), 1);
              const w = Math.max((v30 / max) * 100, 4);
              return (
                <div key={t} className="grid grid-cols-[160px_1fr_60px] gap-2 items-center text-xs">
                  <span className="truncate">{TYPE_LABEL[t] || t}</span>
                  <div className="bg-muted h-2.5 rounded-sm overflow-hidden">
                    <div className="bg-gradient-to-r from-cyan-500 to-emerald-500 h-full" style={{ width: `${w}%` }} />
                  </div>
                  <span className="text-right tabular-nums">
                    <strong>{v30}</strong>
                    <span className="text-[10px] text-muted-foreground ml-1">({v7} 7gg)</span>
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {stats.recent && stats.recent.length > 0 && (
        <details className="border border-border rounded-md">
          <summary className="cursor-pointer text-xs px-3 py-2 hover:bg-muted/50 select-none">
            📜 Ultime {stats.recent.length} email inviate
          </summary>
          <div className="divide-y divide-border max-h-64 overflow-auto">
            {stats.recent.map((r, i) => (
              <div key={`${r.sent_at}-${i}`} className="px-3 py-2 text-[11px] flex items-center gap-2">
                <span className="font-mono text-muted-foreground w-28 shrink-0">
                  {new Date(r.sent_at).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                </span>
                <span className="px-1.5 py-0.5 bg-muted rounded text-[9px] uppercase shrink-0">{r.tipo}</span>
                <span className="truncate text-muted-foreground">{r.to}</span>
                <span className="truncate flex-1">{r.subject}</span>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

export default function ImpostazioniPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingNotifiche, setSavingNotifiche] = useState(false);
  const fileInputRef = useRef(null);
  const [utenti, setUtenti] = useState([]);
  const [formData, setFormData] = useState({
    nome_azienda: '',
    indirizzo: '',
    citta: '',
    provincia: '',
    cap: '',
    telefono: '',
    email: '',
    email_assistenza: '',
    pec: '',
    partita_iva: '',
    codice_fiscale: '',
    codice_sdi: '',
    iban: '',
    banca: '',
    iban2: '',
    banca2: '',
    logo_base64: null,
    resend_api_key: '',
    resend_from_email: '',
    resend_from_name: ''
  });
  const [configScorta, setConfigScorta] = useState({
    attivo: true,
    destinatari_ids: []
  });
  const [permessiDocumenti, setPermessiDocumenti] = useState({
    ddt: { operatore: false, tecnico: false },
    fattura: { operatore: false, tecnico: false },
    nota_credito: { operatore: false, tecnico: false }
  });
  const [savingPermessi, setSavingPermessi] = useState(false);
  const [integrazioneFatturazione, setIntegrazioneFatturazione] = useState({
    provider: '',
    api_key: '',
    api_secret: '',
    ambiente: 'test',
    attivo: false
  });
  const [savingIntegrazione, setSavingIntegrazione] = useState(false);

  useEffect(() => {
    fetchImpostazioni();
    fetchUtenti();
    fetchConfigScorta();
    fetchPermessiDocumenti();
    fetchIntegrazioneFatturazione();
  }, []);

  const fetchImpostazioni = async () => {
    try {
      const response = await axios.get(`${API}/impostazioni`);
      if (response.data && Object.keys(response.data).length > 0) {
        setFormData({
          nome_azienda: response.data.nome_azienda || '',
          indirizzo: response.data.indirizzo || '',
          citta: response.data.citta || '',
          provincia: response.data.provincia || '',
          cap: response.data.cap || '',
          telefono: response.data.telefono || '',
          email: response.data.email || '',
          pec: response.data.pec || '',
          partita_iva: response.data.partita_iva || '',
          codice_fiscale: response.data.codice_fiscale || '',
          codice_sdi: response.data.codice_sdi || '',
          iban: response.data.iban || '',
          banca: response.data.banca || '',
          iban2: response.data.iban2 || '',
          banca2: response.data.banca2 || '',
          logo_base64: response.data.logo_base64 || null,
          resend_api_key: response.data.resend_api_key || '',
          resend_from_email: response.data.resend_from_email || '',
          resend_from_name: response.data.resend_from_name || ''
        });
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUtenti = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUtenti(response.data.filter(u => u.attivo));
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchConfigScorta = async () => {
    try {
      const response = await axios.get(`${API}/notifiche/config/scorta`);
      setConfigScorta({
        attivo: response.data.attivo ?? true,
        destinatari_ids: response.data.destinatari_ids || []
      });
    } catch (error) {
      console.error('Error fetching config scorta:', error);
    }
  };

  const fetchPermessiDocumenti = async () => {
    try {
      const response = await axios.get(`${API}/impostazioni/permessi-documenti`);
      if (response.data) {
        setPermessiDocumenti(response.data);
      }
    } catch (error) {
      console.error('Error fetching permessi documenti:', error);
    }
  };

  const fetchIntegrazioneFatturazione = async () => {
    try {
      const response = await axios.get(`${API}/impostazioni/integrazione-fatturazione`);
      if (response.data) {
        setIntegrazioneFatturazione(response.data);
      }
    } catch (error) {
      console.error('Error fetching integrazione fatturazione:', error);
    }
  };

  const handleSavePermessiDocumenti = async () => {
    setSavingPermessi(true);
    try {
      await axios.put(`${API}/impostazioni/permessi-documenti`, permessiDocumenti);
      toast.success('Permessi documenti salvati');
    } catch (error) {
      toast.error(parseApiError(error, 'Errore nel salvataggio'));
    } finally {
      setSavingPermessi(false);
    }
  };

  const handleSaveIntegrazioneFatturazione = async () => {
    setSavingIntegrazione(true);
    try {
      await axios.put(`${API}/impostazioni/integrazione-fatturazione`, integrazioneFatturazione);
      toast.success('Configurazione fatturazione salvata');
    } catch (error) {
      toast.error(parseApiError(error, 'Errore nel salvataggio'));
    } finally {
      setSavingIntegrazione(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/impostazioni`, formData);
      toast.success('Impostazioni salvate');
    } catch (error) {
      toast.error(parseApiError(error, 'Errore nel salvataggio'));
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Il file deve essere un\'immagine');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Il file non deve superare 2MB');
      return;
    }

    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await axios.post(`${API}/impostazioni/logo`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData({ ...formData, logo_base64: response.data.logo_base64 });
      toast.success('Logo caricato');
    } catch (error) {
      toast.error(parseApiError(error, 'Errore caricamento logo'));
    }
  };

  const handleSaveConfigScorta = async () => {
    setSavingNotifiche(true);
    try {
      await axios.put(`${API}/notifiche/config/scorta`, configScorta);
      toast.success('Configurazione notifiche salvata');
    } catch (error) {
      toast.error(parseApiError(error, 'Errore nel salvataggio'));
    } finally {
      setSavingNotifiche(false);
    }
  };

  const toggleDestinatario = (userId) => {
    setConfigScorta(prev => ({
      ...prev,
      destinatari_ids: prev.destinatari_ids.includes(userId)
        ? prev.destinatari_ids.filter(id => id !== userId)
        : [...prev.destinatari_ids, userId]
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-muted-foreground font-mono text-sm">CARICAMENTO...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="impostazioni-page">
      <div>
        <h1 className="text-3xl font-black tracking-tight">Impostazioni</h1>
        <p className="text-muted-foreground text-sm mt-1">Configurazione aziendale</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Logo */}
        <Card className="rounded-none">
          <CardHeader>
            <CardTitle className="text-sm font-mono uppercase tracking-wider flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Logo Aziendale
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-6">
              <div className="w-32 h-32 border border-border flex items-center justify-center bg-muted">
                {formData.logo_base64 ? (
                  <img 
                    src={`data:image/png;base64,${formData.logo_base64}`} 
                    alt="Logo" 
                    className="max-w-full max-h-full object-contain"
                  />
                ) : (
                  <span className="text-muted-foreground text-xs font-mono">NESSUN LOGO</span>
                )}
              </div>
              <div className="space-y-2">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleLogoUpload}
                  accept="image/*"
                  className="hidden"
                />
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => fileInputRef.current?.click()}
                  className="rounded-none"
                  data-testid="upload-logo-btn"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Carica Logo
                </Button>
                <p className="text-xs text-muted-foreground">
                  Formati: PNG, JPG, GIF. Max 2MB
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Dati Aziendali */}
        <Card className="rounded-md">
          <CardHeader>
            <CardTitle className="text-sm font-mono uppercase tracking-wider flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Dati Aziendali
            </CardTitle>
            <CardDescription>
              Questi dati appariranno su tutti i documenti (Fatture, DDT, Preventivi)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Nome e Indirizzo */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label className="text-xs font-mono uppercase">Ragione Sociale / Nome Azienda *</Label>
                <Input
                  value={formData.nome_azienda}
                  onChange={(e) => setFormData({...formData, nome_azienda: e.target.value})}
                  className="rounded-md text-lg font-bold"
                  placeholder="La Mia Azienda S.r.l."
                  data-testid="impostazioni-nome"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs font-mono uppercase">Indirizzo Sede Legale</Label>
                <Input
                  value={formData.indirizzo}
                  onChange={(e) => setFormData({...formData, indirizzo: e.target.value})}
                  className="rounded-md"
                  placeholder="Via Roma, 123"
                  data-testid="impostazioni-indirizzo"
                />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase">Città</Label>
                <Input
                  value={formData.citta}
                  onChange={(e) => setFormData({...formData, citta: e.target.value})}
                  className="rounded-md"
                  placeholder="Milano"
                  data-testid="impostazioni-citta"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs font-mono uppercase">Provincia</Label>
                  <Input
                    value={formData.provincia}
                    onChange={(e) => setFormData({...formData, provincia: e.target.value.toUpperCase()})}
                    className="rounded-md uppercase"
                    placeholder="MI"
                    maxLength={2}
                    data-testid="impostazioni-provincia"
                  />
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase">CAP</Label>
                  <Input
                    value={formData.cap}
                    onChange={(e) => setFormData({...formData, cap: e.target.value})}
                    className="rounded-md font-mono"
                    placeholder="20100"
                    maxLength={5}
                    data-testid="impostazioni-cap"
                  />
                </div>
              </div>
            </div>

            {/* Dati Fiscali */}
            <div className="border-t pt-4">
              <h3 className="text-xs font-mono uppercase text-muted-foreground mb-3 flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Dati Fiscali
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-mono uppercase">Partita IVA *</Label>
                  <Input
                    value={formData.partita_iva}
                    onChange={(e) => setFormData({...formData, partita_iva: e.target.value})}
                    className="rounded-md font-mono"
                    placeholder="IT12345678901"
                    data-testid="impostazioni-piva"
                  />
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase">Codice Fiscale</Label>
                  <Input
                    value={formData.codice_fiscale}
                    onChange={(e) => setFormData({...formData, codice_fiscale: e.target.value.toUpperCase()})}
                    className="rounded-md font-mono uppercase"
                    placeholder="12345678901"
                    data-testid="impostazioni-cf"
                  />
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase">Codice SDI (Fatturazione Elettronica)</Label>
                  <Input
                    value={formData.codice_sdi}
                    onChange={(e) => setFormData({...formData, codice_sdi: e.target.value.toUpperCase()})}
                    className="rounded-md font-mono uppercase"
                    placeholder="0000000"
                    maxLength={7}
                    data-testid="impostazioni-sdi"
                  />
                </div>
              </div>
            </div>

            {/* Contatti */}
            <div className="border-t pt-4">
              <h3 className="text-xs font-mono uppercase text-muted-foreground mb-3 flex items-center gap-2">
                Contatti
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-mono uppercase">Telefono</Label>
                  <Input
                    value={formData.telefono}
                    onChange={(e) => setFormData({...formData, telefono: e.target.value})}
                    className="rounded-md"
                    placeholder="+39 02 12345678"
                    data-testid="impostazioni-telefono"
                  />
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase">Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="rounded-md"
                    placeholder="info@azienda.it"
                    data-testid="impostazioni-email"
                  />
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase">Email Assistenza / Appuntamenti</Label>
                  <Input
                    type="email"
                    value={formData.email_assistenza}
                    onChange={(e) => setFormData({...formData, email_assistenza: e.target.value})}
                    className="rounded-md"
                    placeholder="assistenza@azienda.it"
                    data-testid="impostazioni-email-assistenza"
                  />
                  <p className="text-[10px] text-muted-foreground mt-1">Mostrata nelle email automatiche di scadenza come contatto alternativo</p>
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs font-mono uppercase">PEC (Posta Elettronica Certificata)</Label>
                  <Input
                    type="email"
                    value={formData.pec}
                    onChange={(e) => setFormData({...formData, pec: e.target.value})}
                    className="rounded-md"
                    placeholder="azienda@pec.it"
                    data-testid="impostazioni-pec"
                  />
                </div>
              </div>
            </div>

            {/* Dati Bancari */}
            <div className="border-t pt-4">
              <h3 className="text-xs font-mono uppercase text-muted-foreground mb-3 flex items-center gap-2">
                Dati Bancari (per pagamenti)
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label className="text-xs font-mono uppercase">IBAN</Label>
                  <Input
                    value={formData.iban}
                    onChange={(e) => setFormData({...formData, iban: e.target.value.toUpperCase().replace(/\s/g, '')})}
                    className="rounded-md font-mono uppercase tracking-wider"
                    placeholder="IT60X0542811101000000123456"
                    data-testid="impostazioni-iban"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs font-mono uppercase">Banca / Istituto di Credito</Label>
                  <Input
                    value={formData.banca}
                    onChange={(e) => setFormData({...formData, banca: e.target.value})}
                    className="rounded-md"
                    placeholder="Banca Intesa Sanpaolo - Filiale di Milano"
                    data-testid="impostazioni-banca"
                  />
                </div>
                <div className="md:col-span-2 border-t border-dashed pt-3 mt-1">
                  <Label className="text-xs font-mono uppercase">IBAN 2 (seconda banca — opzionale)</Label>
                  <Input
                    value={formData.iban2}
                    onChange={(e) => setFormData({...formData, iban2: e.target.value.toUpperCase().replace(/\s/g, '')})}
                    className="rounded-md font-mono uppercase tracking-wider"
                    placeholder="Lascia vuoto se non usata"
                    data-testid="impostazioni-iban2"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs font-mono uppercase">Banca 2 / Istituto di Credito</Label>
                  <Input
                    value={formData.banca2}
                    onChange={(e) => setFormData({...formData, banca2: e.target.value})}
                    className="rounded-md"
                    placeholder="Nome della seconda banca"
                    data-testid="impostazioni-banca2"
                  />
                  <p className="text-[11px] text-muted-foreground mt-1">Se compilata, apparirà insieme alla prima banca su fatture, preventivi, DDT, contratti e rapportini.</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Email - Resend */}
        <Card className="rounded-none border-l-4 border-l-green-500">
          <CardHeader>
            <CardTitle className="text-base font-mono uppercase tracking-wider flex items-center gap-2">
              📧 Email Transazionali (Resend)
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              Configura l&apos;invio email automatico dal CRM (notifiche cliente, accettazione preventivi, conferme).
              {' '}<a href="https://resend.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-green-600 underline">Ottieni API Key da Resend &rarr;</a>
            </p>
          </CardHeader>
          <CardContent>
            <EmailStatsPanel />
            <div className="border-t border-border my-4"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label className="text-xs font-mono uppercase">API Key Resend *</Label>
                <Input
                  type="password"
                  value={formData.resend_api_key || ''}
                  onChange={(e) => setFormData({...formData, resend_api_key: e.target.value})}
                  className="rounded-md font-mono"
                  placeholder="re_xxxxxxxxxxxxxxxxxxxxxxxxxx"
                  data-testid="impostazioni-resend-apikey"
                />
                <p className="text-[10px] text-muted-foreground mt-1">La chiave viene salvata cifrata e usata solo lato backend.</p>
              </div>
              <div>
                <Label className="text-xs font-mono uppercase">Email mittente *</Label>
                <Input
                  value={formData.resend_from_email || ''}
                  onChange={(e) => setFormData({...formData, resend_from_email: e.target.value})}
                  className="rounded-md"
                  placeholder="noreply@veltasistemi.it"
                  data-testid="impostazioni-resend-from"
                />
                <p className="text-[10px] text-muted-foreground mt-1">Deve appartenere al dominio verificato in Resend.</p>
              </div>
              <div>
                <Label className="text-xs font-mono uppercase">Nome mittente</Label>
                <Input
                  value={formData.resend_from_name || ''}
                  onChange={(e) => setFormData({...formData, resend_from_name: e.target.value})}
                  className="rounded-md"
                  placeholder="VELTA SISTEMI"
                  data-testid="impostazioni-resend-name"
                />
              </div>
              <div className="md:col-span-2 flex flex-wrap gap-2 mt-2">
                <Button
                  type="button"
                  variant="outline"
                  className="rounded-md text-xs"
                  onClick={async () => {
                    const dest = prompt("A quale indirizzo email vuoi inviare il test?", formData.email || formData.resend_from_email || '');
                    if (!dest) return;
                    try {
                      toast.loading('Invio email di test in corso...', { id: 'test-email' });
                      const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/email/test`, { to: dest });
                      toast.success(`Email di test inviata a ${res.data.to || dest}! Controlla la casella (anche spam).`, { id: 'test-email' });
                    } catch (err) {
                      toast.error(parseApiError(err, 'Errore invio email di test'), { id: 'test-email' });
                    }
                  }}
                  disabled={!formData.resend_api_key || !formData.resend_from_email}
                  data-testid="test-email-btn"
                >
                  🚀 Invia email di test
                </Button>
                <p className="text-[10px] text-muted-foreground self-center">
                  Prima salva le impostazioni qui sotto, poi prova l&apos;invio. Il test conferma che API Key + dominio sono OK.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Button type="submit" className="rounded-none font-mono uppercase" disabled={saving} data-testid="save-impostazioni-btn">
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Salvataggio...' : 'Salva Impostazioni'}
          </Button>
        </div>
      </form>

      {/* Configurazione Notifiche Scorta Minima */}
      <Card className="rounded-none">
        <CardHeader>
          <CardTitle className="text-sm font-mono uppercase tracking-wider flex items-center gap-2">
            <Package className="h-4 w-4" />
            Notifiche Scorta Minima Magazzino
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-medium">Notifiche Attive</Label>
              <p className="text-xs text-muted-foreground">
                Invia notifiche quando un articolo va sotto scorta minima
              </p>
            </div>
            <Switch
              checked={configScorta.attivo}
              onCheckedChange={(checked) => setConfigScorta({...configScorta, attivo: checked})}
              data-testid="notifiche-scorta-attivo"
            />
          </div>

          {configScorta.attivo && (
            <div className="border border-border p-4 space-y-3">
              <Label className="text-xs font-mono uppercase flex items-center gap-2">
                <Bell className="h-4 w-4" />
                Chi riceve le notifiche?
              </Label>
              <p className="text-xs text-muted-foreground">
                Seleziona gli utenti che riceveranno l'avviso quando un articolo scende sotto la scorta minima.
                Se non selezioni nessuno, le notifiche andranno a tutti gli admin.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3">
                {utenti.map((utente) => (
                  <div 
                    key={utente.id} 
                    className={`flex items-center gap-3 p-2 border border-border cursor-pointer hover:bg-muted/50 transition-colors ${
                      configScorta.destinatari_ids.includes(utente.id) ? 'bg-primary/10 border-primary' : ''
                    }`}
                    onClick={() => toggleDestinatario(utente.id)}
                  >
                    <Checkbox
                      checked={configScorta.destinatari_ids.includes(utente.id)}
                      onCheckedChange={() => toggleDestinatario(utente.id)}
                      data-testid={`notifica-dest-${utente.id}`}
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium">{utente.nome} {utente.cognome}</p>
                      <p className="text-xs text-muted-foreground capitalize">{utente.ruolo}</p>
                    </div>
                    {utente.colore && (
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: utente.colore }}
                      />
                    )}
                  </div>
                ))}
              </div>

              {configScorta.destinatari_ids.length > 0 && (
                <p className="text-xs text-muted-foreground mt-2">
                  {configScorta.destinatari_ids.length} utente/i selezionato/i
                </p>
              )}
            </div>
          )}

          <div className="flex justify-end">
            <Button 
              onClick={handleSaveConfigScorta} 
              className="rounded-md font-mono uppercase" 
              disabled={savingNotifiche}
              data-testid="save-config-notifiche-btn"
            >
              <Save className="h-4 w-4 mr-2" />
              {savingNotifiche ? 'Salvataggio...' : 'Salva Configurazione Notifiche'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Integrazione Fatturazione Elettronica */}
      <Card className="rounded-md">
        <CardHeader>
          <CardTitle className="text-sm font-mono uppercase tracking-wider flex items-center gap-2">
            <Link2 className="h-4 w-4" />
            Integrazione Fatturazione Elettronica
          </CardTitle>
          <CardDescription>
            Collega il tuo provider di fatturazione per invio automatico al SDI. Ogni azienda può scegliere il proprio provider.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Info box */}
          <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-md">
            <p className="text-xs text-amber-700">
              <strong>Nota:</strong> L'integrazione vera e propria verrà attivata quando configurerai le credenziali API del tuo provider. 
              Potrai scegliere tra diversi provider italiani in base alle tue esigenze.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-mono uppercase">Provider Fatturazione</Label>
              <Select 
                value={integrazioneFatturazione.provider || "none"} 
                onValueChange={(v) => setIntegrazioneFatturazione({
                  ...integrazioneFatturazione, 
                  provider: v === "none" ? "" : v
                })}
              >
                <SelectTrigger className="rounded-md" data-testid="provider-fatturazione">
                  <SelectValue placeholder="Seleziona provider..." />
                </SelectTrigger>
                <SelectContent className="rounded-md">
                  <SelectItem value="none">Nessuno (da configurare in seguito)</SelectItem>
                  <SelectItem value="fatture_in_cloud">
                    <span className="flex items-center gap-2">
                      Fatture in Cloud
                      <Badge variant="outline" className="text-[10px] bg-green-100 text-green-700">Popolare</Badge>
                    </span>
                  </SelectItem>
                  <SelectItem value="aruba">Aruba Fatturazione Elettronica</SelectItem>
                  <SelectItem value="teamsystem">TeamSystem Digital</SelectItem>
                  <SelectItem value="zucchetti">Zucchetti Digital Hub</SelectItem>
                  <SelectItem value="danea">Danea Easyfatt</SelectItem>
                  <SelectItem value="passepartout">Passepartout Mexal</SelectItem>
                  <SelectItem value="infocert">InfoCert Legalinvoice</SelectItem>
                  <SelectItem value="wolters_kluwer">Wolters Kluwer Arca</SelectItem>
                  <SelectItem value="namirial">Namirial FEA</SelectItem>
                  <SelectItem value="altro">Altro (configurazione manuale)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs font-mono uppercase">Ambiente</Label>
              <Select 
                value={integrazioneFatturazione.ambiente} 
                onValueChange={(v) => setIntegrazioneFatturazione({...integrazioneFatturazione, ambiente: v})}
              >
                <SelectTrigger className="rounded-md">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-md">
                  <SelectItem value="test">
                    <span className="flex items-center gap-2">
                      Test (Sandbox)
                      <Badge variant="outline" className="text-[10px]">Consigliato per iniziare</Badge>
                    </span>
                  </SelectItem>
                  <SelectItem value="produzione">Produzione (SDI reale)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {integrazioneFatturazione.provider && integrazioneFatturazione.provider !== 'none' && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-mono uppercase">
                    {integrazioneFatturazione.provider === 'fatture_in_cloud' ? 'API UID' : 
                     integrazioneFatturazione.provider === 'aruba' ? 'Username API' : 
                     'API Key / Client ID'}
                  </Label>
                  <Input
                    type="password"
                    value={integrazioneFatturazione.api_key}
                    onChange={(e) => setIntegrazioneFatturazione({
                      ...integrazioneFatturazione, 
                      api_key: e.target.value
                    })}
                    className="rounded-md font-mono"
                    placeholder="Inserisci credenziale..."
                    data-testid="api-key-fatturazione"
                  />
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase">
                    {integrazioneFatturazione.provider === 'fatture_in_cloud' ? 'API Key' : 
                     integrazioneFatturazione.provider === 'aruba' ? 'Password API' : 
                     'API Secret / Client Secret'}
                  </Label>
                  <Input
                    type="password"
                    value={integrazioneFatturazione.api_secret}
                    onChange={(e) => setIntegrazioneFatturazione({
                      ...integrazioneFatturazione, 
                      api_secret: e.target.value
                    })}
                    className="rounded-md font-mono"
                    placeholder="Inserisci credenziale..."
                    data-testid="api-secret-fatturazione"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-muted/50 rounded-md">
                <div>
                  <Label className="text-sm font-medium">Integrazione Attiva</Label>
                  <p className="text-xs text-muted-foreground">
                    Abilita per inviare automaticamente le fatture al provider selezionato
                  </p>
                </div>
                <Switch
                  checked={integrazioneFatturazione.attivo}
                  onCheckedChange={(checked) => setIntegrazioneFatturazione({
                    ...integrazioneFatturazione, 
                    attivo: checked
                  })}
                  data-testid="integrazione-attiva"
                />
              </div>

              {/* Stato connessione */}
              {integrazioneFatturazione.provider === 'aruba' ? (
                <ArubaStatoReale />
              ) : (
              <div className={`p-3 rounded-md flex items-center gap-3 ${
                integrazioneFatturazione.attivo && integrazioneFatturazione.api_key 
                  ? 'bg-green-500/10 border border-green-500/30' 
                  : 'bg-gray-500/10 border border-gray-500/30'
              }`}>
                <div className={`w-3 h-3 rounded-full ${
                  integrazioneFatturazione.attivo && integrazioneFatturazione.api_key 
                    ? 'bg-green-500 animate-pulse' 
                    : 'bg-gray-400'
                }`} />
                <span className="text-sm">
                  {integrazioneFatturazione.attivo && integrazioneFatturazione.api_key 
                    ? 'Configurazione pronta - L\'integrazione verrà attivata al salvataggio' 
                    : 'Integrazione non configurata'}
                </span>
              </div>
              )}

              {/* Istruzioni per ottenere le credenziali */}
              <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-md">
                <p className="text-sm font-medium text-blue-600 mb-2">Come ottenere le credenziali API?</p>
                <ul className="text-xs text-muted-foreground space-y-1">
                  {integrazioneFatturazione.provider === 'fatture_in_cloud' && (
                    <>
                      <li>1. Accedi a <a href="https://www.fattureincloud.it" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline inline-flex items-center gap-1">Fatture in Cloud <ExternalLink className="h-3 w-3" /></a></li>
                      <li>2. Vai su Impostazioni → API → Crea nuova App</li>
                      <li>3. Copia API UID e API Key qui sopra</li>
                    </>
                  )}
                  {integrazioneFatturazione.provider === 'aruba' && (
                    <>
                      <li>1. Accedi al pannello <a href="https://fatturazione.aruba.it" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline inline-flex items-center gap-1">Aruba Fatturazione <ExternalLink className="h-3 w-3" /></a></li>
                      <li>2. Vai su Configurazione → Integrazioni → API</li>
                      <li>3. Genera le credenziali API e inseriscile qui</li>
                    </>
                  )}
                  {integrazioneFatturazione.provider === 'teamsystem' && (
                    <>
                      <li>1. Contatta il supporto <a href="https://www.teamsystem.com" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline inline-flex items-center gap-1">TeamSystem <ExternalLink className="h-3 w-3" /></a> per l'attivazione API</li>
                      <li>2. Riceverai le credenziali via email</li>
                      <li>3. Inseriscile nei campi qui sopra</li>
                    </>
                  )}
                  {integrazioneFatturazione.provider === 'zucchetti' && (
                    <>
                      <li>1. Accedi al portale <a href="https://www.zucchetti.it" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline inline-flex items-center gap-1">Zucchetti <ExternalLink className="h-3 w-3" /></a></li>
                      <li>2. Richiedi l'attivazione del modulo Digital Hub API</li>
                      <li>3. Configura le credenziali ricevute</li>
                    </>
                  )}
                  {integrazioneFatturazione.provider === 'danea' && (
                    <>
                      <li>1. Accedi a <a href="https://www.danea.it/software/easyfatt/" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline inline-flex items-center gap-1">Danea Easyfatt <ExternalLink className="h-3 w-3" /></a></li>
                      <li>2. Vai su Strumenti → Opzioni → Fatturazione Elettronica</li>
                      <li>3. Configura le credenziali del servizio SDI</li>
                    </>
                  )}
                  {integrazioneFatturazione.provider === 'passepartout' && (
                    <>
                      <li>1. Contatta il tuo rivenditore <a href="https://www.passepartout.net" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline inline-flex items-center gap-1">Passepartout <ExternalLink className="h-3 w-3" /></a></li>
                      <li>2. Richiedi l'attivazione modulo Fatturazione Elettronica</li>
                      <li>3. Configura le credenziali nel portale Mexal</li>
                    </>
                  )}
                  {integrazioneFatturazione.provider === 'infocert' && (
                    <>
                      <li>1. Accedi a <a href="https://www.infocert.it/prodotti/legalinvoice/" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline inline-flex items-center gap-1">InfoCert Legalinvoice <ExternalLink className="h-3 w-3" /></a></li>
                      <li>2. Richiedi le credenziali API dal pannello di controllo</li>
                      <li>3. Inserisci Client ID e Client Secret</li>
                    </>
                  )}
                  {integrazioneFatturazione.provider === 'wolters_kluwer' && (
                    <>
                      <li>1. Contatta <a href="https://www.wolterskluwer.com/it-it" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline inline-flex items-center gap-1">Wolters Kluwer <ExternalLink className="h-3 w-3" /></a></li>
                      <li>2. Attiva il modulo Fatturazione Elettronica su Arca</li>
                      <li>3. Configura le credenziali API fornite</li>
                    </>
                  )}
                  {integrazioneFatturazione.provider === 'namirial' && (
                    <>
                      <li>1. Accedi a <a href="https://www.namirial.com" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline inline-flex items-center gap-1">Namirial <ExternalLink className="h-3 w-3" /></a></li>
                      <li>2. Richiedi attivazione servizio FEA</li>
                      <li>3. Inserisci le credenziali ricevute</li>
                    </>
                  )}
                  {integrazioneFatturazione.provider === 'altro' && (
                    <>
                      <li>Per integrazioni personalizzate, contatta il supporto tecnico</li>
                      <li>Fornisci: nome provider, documentazione API, credenziali</li>
                    </>
                  )}
                </ul>
              </div>
            </>
          )}

          {/* Messaggio se nessun provider selezionato */}
          {(!integrazioneFatturazione.provider || integrazioneFatturazione.provider === 'none') && (
            <div className="p-4 bg-muted/50 rounded-md text-center">
              <Receipt className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">
                Seleziona un provider per configurare l'integrazione con la fatturazione elettronica.
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Potrai sempre modificare questa scelta in seguito.
              </p>
            </div>
          )}

          <div className="flex justify-end">
            <Button 
              onClick={handleSaveIntegrazioneFatturazione} 
              className="rounded-md font-mono uppercase" 
              disabled={savingIntegrazione}
              data-testid="save-integrazione-fatturazione-btn"
            >
              <Save className="h-4 w-4 mr-2" />
              {savingIntegrazione ? 'Salvataggio...' : 'Salva Configurazione'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
